import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import NotificationManager from '../common/NotificationManager';
import SupportManager from '../common/SupportManager';
import UserCardModal from '../common/UserCardModal';
import {
  ChartBarIcon,
  HomeIcon,
  Bars3Icon,
  XMarkIcon,
  DocumentTextIcon,
  ChartPieIcon,
  BellIcon,
  LightBulbIcon,
  CalendarIcon,
  KeyIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ScaleIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userCardModalOpen, setUserCardModalOpen] = useState(false);

  // Debug logging
  console.log('🔍 DashboardLayout - User:', user);
  console.log('🔍 DashboardLayout - User ID:', user?.id);
  console.log('🔍 DashboardLayout - User authenticated:', !!user?.id);

  const getNavigationItems = () => {
    const userPlan = user?.subscription?.plan_tier || user?.plan || 'free';
    
    // Simplified navigation - only core items
    const allItems = [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, tier: 'starter' },
      { name: 'Trends', href: '/trends', icon: ChartBarIcon, tier: 'starter' },
      { name: 'Analytics', href: '/analytics', icon: ChartPieIcon, tier: 'starter' },
      { name: 'Alerts', href: '/alerts', icon: BellIcon, tier: 'growth' },
      { name: 'Reports', href: '/reports', icon: DocumentTextIcon, tier: 'growth' },
      { name: 'API Keys', href: '/api-keys', icon: KeyIcon, tier: 'starter' },
    ];

    // Filter items based on user's tier
    const tierLevels = { starter: 1, growth: 2, professional: 3, business: 4 };
    const userTierLevel = tierLevels[userPlan as keyof typeof tierLevels] || 1;
    
    return allItems.map(item => {
      const itemTierLevel = tierLevels[item.tier as keyof typeof tierLevels] || 1;
      const hasAccess = userTierLevel >= itemTierLevel;
      
      return {
        ...item,
        hasAccess,
        upgradeRequired: !hasAccess,
        category: item.tier === 'starter' ? 'core' : 
                 item.tier === 'growth' ? 'growth' :
                 item.tier === 'professional' ? 'professional' : 'business'
      };
    });
  };

  const navigation = getNavigationItems();

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4 flex-shrink-0">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">T</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">TrendBeat</span>
              </Link>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-900"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <nav className="space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.hasAccess ? (
                  <Link
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive(item.href)
                        ? 'bg-purple-100 text-purple-900'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 ${
                        isActive(item.href) ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    {item.name}
                  </Link>
                ) : (
                  <Link
                    to={item.href}
                    className="group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 hover:bg-gray-50 transition-colors relative"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <LockClosedIcon className="mr-3 h-5 w-5 text-gray-300 group-hover:text-gray-400" />
                    <span className="flex-1">{item.name}</span>
                  </Link>
                )}
              </div>
            ))}
            </nav>
            
            {/* Notifications Section - Above User Card */}
            {user?.id && (
              <div className="px-2 py-2">
                <NotificationManager userId={user.id} variant="sidebar" />
              </div>
            )}
            
            {/* Support Section - Below Notifications */}
            <div className="px-2 py-2">
              <SupportManager variant="sidebar" />
            </div>
          </div>
          
          <div className="border-t border-gray-200 p-4 flex-shrink-0">
            <button
              onClick={() => setUserCardModalOpen(true)}
              className="w-full text-left p-2 hover:bg-gray-50 rounded-md group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">T</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">TrendBeat</span>
              </Link>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.hasAccess ? (
                  <Link
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive(item.href)
                        ? 'bg-purple-100 text-purple-900'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 ${
                        isActive(item.href) ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    {item.name}
                  </Link>
                ) : (
                  <Link
                    to={item.href}
                    className="group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 hover:bg-gray-50 transition-colors relative"
                  >
                    <LockClosedIcon className="mr-3 h-5 w-5 text-gray-300 group-hover:text-gray-400" />
                    <span className="flex-1">{item.name}</span>
                  </Link>
                )}
              </div>
            ))}
          </nav>
          
          {/* Notifications Section - Above User Card */}
          {user?.id && (
            <div className="px-2 py-2">
              <NotificationManager userId={user.id} variant="sidebar" />
            </div>
          )}
          
          {/* Support Section - Below Notifications */}
          <div className="px-2 py-2">
            <SupportManager variant="sidebar" />
          </div>
          
          
          <div className="p-4">
            <button
              onClick={() => setUserCardModalOpen(true)}
              className="w-full text-left p-2 hover:bg-gray-50 rounded-md group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 pt-0">
        {/* Mobile sidebar toggle - only visible on mobile */}
        <div className="lg:hidden fixed top-4 left-4 z-30">
          <button
            type="button"
            className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-5 w-5 text-gray-700" />
          </button>
        </div>

        {/* Page content */}
        <main className="pt-0 pb-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* User Card Modal */}
      <UserCardModal
        isOpen={userCardModalOpen}
        onClose={() => setUserCardModalOpen(false)}
        user={user}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default DashboardLayout; 